import type { AccessibleModule, AccessiblePages, AccountType, FeatureName, PagePermission } from "@shared/types";

/**
 * Sample RBAC permissions for different feature types
 * These can be used in Storybook stories and tests
 */

// ============================================================================
// Permission Configurations
// ============================================================================

/**
 * BLP (Beanz Label Print) - Focus on order processing and label printing
 * Source: Salesforce default permissions for BLP account type
 */
export const sampleBLPPermissions: AccessiblePages = {
  pages: [
    { page: "batch", actions: ["view"] },
    { page: "finance", actions: ["export", "view"] },
    { page: "finance.invoices", actions: ["export", "view"] },
    { page: "home", actions: ["view"] },
    { page: "labels", actions: ["view"] },
    { page: "orders", actions: ["export", "print", "view"] },
    { page: "products", actions: ["create", "edit", "view"] },
    { page: "settings", actions: ["view", "edit"] },
  ],
  menuGroups: ["batch", "finance", "home", "labels", "orders", "products", "settings"],
};

/**
 * SS (ShipStation) - Access to finance, home, orders, products, and settings
 * Source: Salesforce default permissions for ShipStation account type
 */
export const sampleSSPermissions: AccessiblePages = {
  pages: [
    { page: "finance", actions: ["export", "view"] },
    { page: "finance.invoices", actions: ["export", "view"] },
    { page: "home", actions: ["view"] },
    { page: "orders", actions: ["export", "print", "view"] },
    { page: "products", actions: ["create", "edit", "view"] },
    { page: "settings", actions: ["view", "edit"] },
  ],
  menuGroups: ["finance", "home", "orders", "products", "settings"],
};

/**
 * BC OG (Beanz Connect Original / non_blp) - Access to finance, home, orders, products, and settings (can edit settings)
 * Source: Salesforce default permissions for non_blp account type
 */
export const sampleBCOGPermissions: AccessiblePages = {
  pages: [
    { page: "finance", actions: ["export", "view"] },
    { page: "finance.invoices", actions: ["export", "view"] },
    { page: "home", actions: ["view"] },
    { page: "orders", actions: ["export", "print", "view"] },
    { page: "products", actions: ["create", "edit", "view"] },
    { page: "settings", actions: ["edit", "view"] },
  ],
  menuGroups: ["finance", "home", "orders", "products", "settings"],
};

/**
 * Read-only user - Can only view, no create/edit permissions
 */
export const sampleReadOnlyPermissions: AccessiblePages = {
  pages: [
    { page: "dashboard", actions: ["view"] },
    { page: "finance", actions: ["view"] },
    { page: "finance.invoices", actions: ["view"] },
    { page: "finance.settings", actions: ["view"] },
    { page: "home", actions: ["view"] },
    { page: "notifications", actions: ["view"] },
    { page: "orders", actions: ["view"] },
    { page: "products", actions: ["view"] },
    { page: "settings", actions: ["view"] },
  ],
  menuGroups: ["dashboard", "finance", "home", "notifications", "orders", "products", "settings"],
};

/**
 * Admin user - Full access including delete operations
 */
export const sampleAdminPermissions: AccessiblePages = {
  pages: [
    { page: "batch", actions: ["view", "create", "edit", "delete", "print"] },
    { page: "dashboard", actions: ["export", "view"] },
    { page: "finance", actions: ["export", "view"] },
    { page: "finance.invoices", actions: ["view", "create", "export"] },
    { page: "finance.settings", actions: ["view", "edit"] },
    { page: "home", actions: ["view"] },
    { page: "labels", actions: ["view", "create", "print"] },
    { page: "notifications", actions: ["view"] },
    {
      page: "orders",
      actions: ["view", "create", "edit", "delete", "export", "print"],
    },
    { page: "products", actions: ["view", "create", "edit", "delete", "sync"] },
    { page: "settings", actions: ["view", "edit"] },
  ],
  menuGroups: ["batch", "dashboard", "finance", "home", "labels", "notifications", "orders", "products", "settings"],
};

// ============================================================================
// Feature Name to Permissions Mapping
// ============================================================================

export const featurePermissionsMap: Record<FeatureName, AccessiblePages> = {
  BLP: sampleBLPPermissions,
  SS: sampleSSPermissions,
  "BC OG": sampleBCOGPermissions,
};

// ============================================================================
// API Response Mocks
// ============================================================================

/**
 * Mock API response for BLP feature configuration
 */
export const mockBLPFeatureResponse = {
  PK: "Feature#BLP",
  SK: "CONFIG",
  featureName: "BLP" as FeatureName,
  pages: sampleBLPPermissions.pages,
  menuGroups: sampleBLPPermissions.menuGroups,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

/**
 * Mock API response for SS feature configuration
 */
export const mockSSFeatureResponse = {
  PK: "Feature#SS",
  SK: "CONFIG",
  featureName: "SS" as FeatureName,
  pages: sampleSSPermissions.pages,
  menuGroups: sampleSSPermissions.menuGroups,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

/**
 * Mock API response for BC OG feature configuration
 */
export const mockBCOGFeatureResponse = {
  PK: "Feature#BC OG",
  SK: "CONFIG",
  featureName: "BC OG" as FeatureName,
  pages: sampleBCOGPermissions.pages,
  menuGroups: sampleBCOGPermissions.menuGroups,
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

// ============================================================================
// Account Type to Permissions Mapping (used for admin impersonation)
// ============================================================================

/**
 * Maps account type (from backend /vendors/impersonate response) to
 * default permissions. Used during roaster admin impersonation to assign
 * static RBAC permissions based on account type instead of fetching from
 * the backend permissions table.
 */
export const accountTypePermissionsMap: Record<AccountType, AccessiblePages> = {
  blp: sampleBLPPermissions,
  shipstation: sampleSSPermissions,
  non_blp: sampleBCOGPermissions,
};

// ============================================================================
// Helper Functions for Stories
// ============================================================================

/**
 * Get permissions for a feature name
 */
export function getPermissionsForFeature(featureName: FeatureName): AccessiblePages {
  return featurePermissionsMap[featureName];
}

/**
 * Check if a feature has access to a specific page
 */
export function featureHasPageAccess(featureName: FeatureName, page: string): boolean {
  const permissions = featurePermissionsMap[featureName];
  return (permissions.pages as PagePermission[]).some((p) => p.page === page);
}

/**
 * Check if a feature has access to a specific menu group
 */
export function featureHasModuleAccess(featureName: FeatureName, module: AccessibleModule): boolean {
  const permissions = featurePermissionsMap[featureName];
  return permissions.menuGroups.includes(module);
}
